import { Avatar } from "@hortis/ui/avatar";
import { IconButton } from "@hortis/ui/button";
import { Dot } from "@hortis/ui/dot";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@hortis/ui/dropdown-menu";
import { DotsHorizontal, Edit3, Trash3 } from "@hortis/ui/icons";
import { RichTextViewer } from "@hortis/ui/rich-text-editor";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { TwicImg } from "@twicpics/components/react";
import { ObservationFieldsFragment } from "generated/graphql";
import { useState } from "react";
import TimeAgo from "react-timeago";
import type { Formatter } from "react-timeago";
import { Lightbox, LightboxTrigger } from "src/components/lightbox";
import { mentionReadonly } from "src/components/rich-text-editor/mention";
import { TrashObservationDialog } from "src/features/collection/components/archive-records/trash-observation-dialog";
import { useAccessRole } from "src/features/permissions/use-access-role";
import { createFullName } from "src/utils/full-name";
import { useDateStringFormatter } from "src/utils/hooks/language";
import { observationConditionLabelMap } from "src/utils/label-maps/observation-condition";
import { twMerge } from "tailwind-merge";
import { UpdateObservationDialog } from "../../create-observation-dialog/edit-observation-dialog";
import { ObservationConditionRating } from "./observation-condition";

const lastObservedTimeAgoFormatter: Formatter = (
  value,
  unit,
  suffix,
  epochMilliseconds,
  nextFormatter,
) =>
  unit === "second"
    ? "Just now"
    : nextFormatter == null
      ? undefined
      : nextFormatter(value, unit, suffix, epochMilliseconds);

const extensions = [mentionReadonly];

export const ObservationContent = ({
  observation,
  hideActions = false,
}: {
  observation: ObservationFieldsFragment;
  hideActions?: boolean;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [trashDialogOpen, setTrashDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { canEdit, userId } = useAccessRole();
  const formatDateTime = useDateStringFormatter({
    dateStyle: "full",
    timeStyle: "long",
    timeZone: undefined,
  });
  const hasPhotosOrBody =
    (observation?.images.nodes != null &&
      observation.images.nodes.length > 0) ||
    (observation.body != null && observation.body !== "");
  return (
    <div className={twMerge("space-y-1", hasPhotosOrBody && "pb-2")}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <div
            className="flex items-center gap-1.5 text-sm font-medium text-grey-500"
            data-cy="last-observation-condition"
          >
            <ObservationConditionRating condition={observation.condition} />
            {observationConditionLabelMap[observation.condition]}
          </div>
          <Dot size="xs" className="bg-grey-300" />
          <div className="flex items-center gap-1.5 text-sm font-medium text-grey-500">
            <Avatar
              size="xxs"
              src={observation.user.avatarUrl}
              textContent={observation.user.initials}
            />
            {createFullName(observation.user)}
          </div>
          <Dot size="xs" className="bg-grey-300" />
          <Tooltip>
            <TooltipTrigger
              data-cy="last-observation-date"
              className="flex items-center gap-1.5 text-sm font-medium text-grey-500"
            >
              <TimeAgo
                date={observation.createdAt}
                formatter={lastObservedTimeAgoFormatter}
                style={{ display: "inline-block" }}
              />
            </TooltipTrigger>
            <TooltipContent>
              {formatDateTime(observation.createdAt) ?? false}
            </TooltipContent>
          </Tooltip>
        </div>
        <TrashObservationDialog
          observationId={observation.id}
          open={trashDialogOpen}
          onOpenChange={setTrashDialogOpen}
        />
        <UpdateObservationDialog
          initialValues={observation}
          observationId={observation.id}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
        />
        {canEdit && observation.user.id === userId && !hideActions && (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <IconButton
                icon={DotsHorizontal}
                ariaLabel="Observation actions"
                size="sm"
                variant="tertiaryGray"
                className={twMerge(
                  "opacity-100 group-hover:opacity-100 lg:opacity-0 transition-opacity pointer-events-auto",
                  menuOpen && "opacity-100",
                )}
                testId="last-observation-actions"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="pointer-events-auto">
              <DropdownMenuItem
                onSelect={() => {
                  setEditDialogOpen(true);
                }}
                testId="last-observation-edit-menu-item"
              >
                <Edit3 className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  setTrashDialogOpen(true);
                }}
                testId="last-observation-delete-menu-item"
              >
                <Trash3 className="w-4 h-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {observation.body != null && observation.body !== "" && (
        <RichTextViewer
          value={observation.bodyData}
          extensions={extensions}
          key={JSON.stringify(observation.bodyData)}
          data-cy="last-observation-body"
        />
      )}
      {observation?.images.nodes != null &&
        observation.images.nodes.length > 0 && (
          <div
            className={twMerge(
              "flex flex-wrap gap-1.5",
              observation.body != null && observation.body !== "" && "pt-2",
            )}
          >
            {observation?.images.nodes.map((image) => (
              <Lightbox photo={image} photos={observation?.images.nodes}>
                <LightboxTrigger
                  data-cy="last-observation-image"
                  className="relative h-12 w-16 overflow-hidden rounded-lg border border-transparent ring-grey-400/60 transition duration-300 animate-in fade-in hover:ring-2 hover:brightness-110"
                >
                  <TwicImg
                    key={image.id}
                    src={`${image.bucketName}/${image.fileName}`}
                    focus="auto"
                    mode="cover"
                  />
                </LightboxTrigger>
              </Lightbox>
            ))}
          </div>
        )}
    </div>
  );
};
